/**
 * Admin portal entry point.
 *
 * Selected at build time via `VUE_APP_BUILD_TARGET=admin` (see vue.config.js,
 * which swaps the webpack entry to this file). Deployed as a separate image on
 * its own subdomain (admin.*), so it never shares the customer SPA's bundle or
 * its per-origin session storage.
 *
 * Reuses the shared chart runtime for inline newsroom articles, alongside
 * Pinia stores, i18n, axios clients and the
 * generic auth-restore controller.
 */
import { createPinia, setActivePinia } from 'pinia';
import { createApp } from 'vue';

import i18n from '@/i18n';
import { initSentry } from '@/plugins/sentry';
import { toastPlugin } from '@/plugins/toast';
import { createInitialAuthRestoreController } from '@/router/guards/authGuard';
import { useAuthStore } from '@/stores/useAuthStore';
import { useDisplayStore } from '@/stores/useDisplayStore';

import '@/styles/css/main.css';
import '@/styles/css/theme.css';
import './styles/admin-density.css';

// Consume + strip any `?token=&refresh_token=` handoff from a customer-app
// deep-link. MUST be imported before `./router` — see the module docstring.
import './bootstrap/sessionHandoff';
import AdminApp from './AdminApp.vue';
import { createManageAccessGuard } from './guards/requiresManageAccess';
import router from './router';

// Keep admin density (including teleported dialogs) out of the portal.
document.documentElement.dataset.admin = 'true';


const pinia = createPinia();
setActivePinia(pinia);

const displayStore = useDisplayStore();
const authStore = useAuthStore();

// Theme + locale, mirroring the customer bootstrap.
displayStore.responsiveLayoutControl();
window.onresize = () => displayStore.responsiveLayoutControl();
displayStore.defaultLocale();
displayStore.defaultTheme();
document.documentElement.lang = i18n.global.locale.value;

// Restore any existing session (this origin has its own token storage), then
// gate every non-public route on super-user access via the manage seam.
const authRestore = createInitialAuthRestoreController({ router, authStore });
authRestore.start();
router.beforeEach(createManageAccessGuard({ authRestore }));

// Recover from stale/missing lazy chunks — e.g. the dev server recompiled
// mid-session, or a new version deployed while the tab was open. Reload once
// (guarded against loops) so the browser fetches the current app.js + chunks
// instead of stranding the user on a ChunkLoadError.
const CHUNK_RELOAD_GUARD = 'adminChunkReloadAttempt';
router.onError((error) => {
  const message = error?.message || '';
  if (
    /loading chunk \S+ failed|chunkloaderror|error loading dynamically imported module/i.test(
      message
    )
  ) {
    if (!sessionStorage.getItem(CHUNK_RELOAD_GUARD)) {
      sessionStorage.setItem(CHUNK_RELOAD_GUARD, '1');
      window.location.reload();
    }
  }
});
router
  .isReady()
  .then(() => sessionStorage.removeItem(CHUNK_RELOAD_GUARD))
  .catch(() => {});

const app = createApp(AdminApp)
  .use(i18n)
  .use(pinia)
  .use(router)
  .use(toastPlugin);

app.config.errorHandler = (err, _instance, info) => {
  console.error('[Admin Vue errorHandler]', info, err);
};

// Sentry hooks Vue's errorHandler automatically once initialised (no-op without a DSN).
initSentry({ app, router });

// Match the widget bootstrap: initialize modules before lazy visualizations mount.
import(/* webpackChunkName: "highcharts-init" */ '@/plugins/highcharts')
  .then(({ Highcharts, HighchartsVue, applyHighchartsTheme }) => {
    app.use(HighchartsVue, { highcharts: Highcharts });
    applyHighchartsTheme();
  })
  .catch((error) => console.error('[Admin charts]', error))
  .finally(() => app.mount('#app'));
